import { useEffect } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { hasAnyPermission, PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
// Deep import: the `@/shared/hooks` barrel re-exports hooks that import
// `@/entities/user`, so going through it would put this store in a cycle back to
// its own slice.
import { usePermissionCheck } from '@/shared/hooks/usePermissionCheck';
import { getScopedPermissions, type PermissionScope } from '@/lib/api/platform/platformSdk';
import { logger } from '@/lib/utils/logger';

export interface ProjectCapabilities {
  canView: boolean;
  canEditDetails: boolean;
  canUploadAssets: boolean;
  canApproveAssets: boolean;
  canComment: boolean;
  canDelete: boolean;
  canManageMembers: boolean;
  /**
   * Create, edit, copy or delete chains. Needs member management too: a step may name a role, and
   * only those who manage members can see who holds it.
   */
  canManageApprovalChains: boolean;
  /** Put a chain on folders and assets, or take it off. */
  canAssignApprovalChains: boolean;
}

/**
 * How long a successful scoped-permission fetch stays fresh. Within this window
 * reads are served from memory — no platform call. After it, the next read of
 * any scoped hook (`useScopedAllow`, for either scope) triggers a background
 * refetch, so a revoked permission is picked up the next time a gated surface
 * renders. Mirrors the app's canonical TanStack `staleTime`.
 */
const FRESH_TTL_MS = 5 * 60 * 1000;

/**
 * Backoff after a failed fetch. Short so a transient platform error recovers
 * quickly, but long enough that repeated reads don't hammer the endpoint.
 */
const ERROR_RETRY_MS = 30 * 1000;

type ScopeKey = Extract<PermissionScope, 'project' | 'workspace'>;

interface ScopeState {
  identifier: string | null;
  permissions: string[];
  fetchedAt: number | null;
  fetching: boolean;
  error: string | null;
}

interface ScopedPermissionsState {
  project: ScopeState;
  workspace: ScopeState;
}

const EMPTY_SCOPE: ScopeState = {
  identifier: null,
  permissions: [],
  fetchedAt: null,
  fetching: false,
  error: null,
};

const useScopedPermissionsStore = create<ScopedPermissionsState>(() => ({
  project: EMPTY_SCOPE,
  workspace: EMPTY_SCOPE,
}));

/** A scope is fresh (no refetch needed) while inside the success TTL, or the
 *  error backoff window after a failure. */
function isScopeFresh(scope: ScopeState): boolean {
  if (scope.fetchedAt === null) return false;
  const ttl = scope.error ? ERROR_RETRY_MS : FRESH_TTL_MS;
  return Date.now() - scope.fetchedAt < ttl;
}

const inFlightScopeFetches = new Map<string, Promise<string[]>>();

async function resolveScopedPermissions(scope: ScopeKey, identifier: string) {
  const current = useScopedPermissionsStore.getState()[scope];

  // Dedupe: skip when the same resource is already in flight, fresh, or inside
  // its error-backoff window. This is what makes many concurrent readers (and
  // repeated renders) collapse to at most one request per window.
  if (current.identifier === identifier && (current.fetching || isScopeFresh(current))) return;

  // A fetch for this exact resource may already be in flight even when the store
  // no longer shows it — e.g. `clearProjectPermissions()` running in an effect
  // cleanup (StrictMode's mount→unmount→mount) resets `fetching`/`identifier`.
  // Re-assert the pending state so the landing response isn't discarded as
  // stale, then piggy-back on the existing request instead of firing a new one.
  const inFlightKey = `${scope}:${identifier}`;
  const pending = inFlightScopeFetches.get(inFlightKey);

  useScopedPermissionsStore.setState({
    [scope]: {
      identifier,
      permissions: current.identifier === identifier ? current.permissions : [],
      fetchedAt: current.identifier === identifier ? current.fetchedAt : null,
      fetching: true,
      error: null,
    },
  });

  if (pending) return;

  const request = getScopedPermissions(scope, identifier);
  inFlightScopeFetches.set(inFlightKey, request);

  try {
    const permissions = await request;
    // Ignore a stale response if the active resource changed mid-flight.
    if (useScopedPermissionsStore.getState()[scope].identifier !== identifier) return;
    useScopedPermissionsStore.setState({
      [scope]: { identifier, permissions, fetchedAt: Date.now(), fetching: false, error: null },
    });
  } catch (err) {
    logger.error(`Failed to resolve ${scope} permissions:`, err);
    const latest = useScopedPermissionsStore.getState()[scope];
    if (latest.identifier !== identifier) return;
    // Keep the last-known-good permissions so a transient refresh failure never
    // strips a permitted user's controls; only an initial load (empty list)
    // default-denies. `fetchedAt` is stamped so the error backoff applies.
    useScopedPermissionsStore.setState({
      [scope]: {
        identifier,
        permissions: latest.permissions,
        fetchedAt: Date.now(),
        fetching: false,
        // eslint-disable-next-line i18next/no-literal-string -- developer diagnostic held in store state, never rendered
        error: err instanceof Error ? err.message : 'Failed to resolve permissions',
      },
    });
  } finally {
    inFlightScopeFetches.delete(inFlightKey);
  }
}

export function resolveProjectPermissions(platformProjectId: string) {
  void resolveScopedPermissions('project', platformProjectId);
}

export function clearProjectPermissions() {
  useScopedPermissionsStore.setState({ project: EMPTY_SCOPE });
}

/**
 * Returns an `allow(permissions)` predicate for the given scope, mirroring the
 * backend's `RequireAnyPermission`. By default the admin override applies
 * (as over `withAdminPermissions(...caps)`), checked against the scope's own
 * `/auth/permissions` list (the platform folds inherited admin roles into that
 * response) — not the flat bootstrap/JWT permissions, which are scope-blind.
 * Passing `adminOverride: false` skips it and matches the listed keys only.
 * Reading this hook lazily refreshes the scope when stale. Before the first
 * resolve the list is empty → default-deny.
 */
function useScopedAllow(
  scopeKind: ScopeKey,
  { adminOverride = true }: { adminOverride?: boolean } = {},
): (permissions: readonly string[]) => boolean {
  const check = usePermissionCheck();
  const scope = useScopedPermissionsStore(useShallow((state) => state[scopeKind]));
  const isStale = scope.identifier !== null && !isScopeFresh(scope);
  useEffect(() => {
    if (scope.identifier && isStale && !scope.fetching) {
      void resolveScopedPermissions(scopeKind, scope.identifier);
    }
  }, [scopeKind, scope.identifier, scope.fetching, isStale]);

  return (permissions) =>
    adminOverride
      ? check(scope.permissions, permissions)
      : hasAnyPermission(scope.permissions, permissions);
}

/**
 * True while a scope is resolving for the first time (or after its identifier
 * changed) and has not yet settled to a permission list. Distinguishes "still
 * loading, don't know yet" from "resolved → denied", so a gate can show a
 * loading affordance instead of a definitive no-permission message. Once a
 * fetch settles (success OR error stamps `fetchedAt`) this is false — a stale
 * background refresh keeps the last-known-good list and is not "resolving" —
 * unless `expectedIdentifier` is given and the stored scope is for a different
 * identifier (e.g. a workspace mismatch), in which case it stays true until the
 * scope switches to that identifier.
 */
function useScopeResolving(scopeKind: ScopeKey, expectedIdentifier?: string | null): boolean {
  return useScopedPermissionsStore((state) => {
    const scope = state[scopeKind];
    if (scope.fetching && scope.fetchedAt === null) return true;
    return !!expectedIdentifier && scope.identifier !== expectedIdentifier;
  });
}

/**
 * True when the caller holds (any of) `permissions` on the active project, with
 * the org/workspace-admin override. Backs the `ScopedPermission` gate component.
 */
export function useHasProjectPermission(permissions: readonly string[] = []): boolean {
  return useScopedAllow('project')(permissions);
}

/** True while the active project's permissions are resolving for the first time. */
export function useIsProjectPermissionsResolving(): boolean {
  return useScopeResolving('project');
}

/** Per-project capability booleans for call sites that thread a boolean prop. */
export function useProjectCapabilities(): ProjectCapabilities {
  const allow = useScopedAllow('project');

  return {
    canView: allow([PROJECT_PERMISSIONS.ProjectView]),
    canEditDetails: allow([PROJECT_PERMISSIONS.ProjectEditDetails]),
    canUploadAssets: allow([PROJECT_PERMISSIONS.ProjectUploadAssets]),
    canApproveAssets: allow([PROJECT_PERMISSIONS.ProjectApproveAssets]),
    canComment: allow([PROJECT_PERMISSIONS.ProjectComment]),
    canDelete: allow([PROJECT_PERMISSIONS.ProjectDelete]),
    canManageMembers: allow([PROJECT_PERMISSIONS.ManageProjectMembers]),
    canManageApprovalChains:
      allow([PROJECT_PERMISSIONS.ManageApprovalChains]) &&
      allow([PROJECT_PERMISSIONS.ManageProjectMembers]),
    canAssignApprovalChains: allow([PROJECT_PERMISSIONS.ManageApprovalChains]),
  };
}

export function resolveWorkspacePermissions(workspaceId: string) {
  void resolveScopedPermissions('workspace', workspaceId);
}

export function clearWorkspacePermissions() {
  useScopedPermissionsStore.setState({ workspace: EMPTY_SCOPE });
}

/**
 * True when the caller holds (any of) `permissions` on the active workspace,
 * with the org/workspace-admin override. Passing no permissions leaves that
 * override as the only gate, which reads as admin-only. The scope is
 * seeded/refreshed by the app shell on workspace change.
 */
export function useHasWorkspacePermission(permissions: readonly string[] = []): boolean {
  return useScopedAllow('workspace')(permissions);
}

/**
 * True when the caller holds (any of) `permissions` on the active workspace, decided by the
 * granted keys alone. Unlike `useHasWorkspacePermission` there is no admin override, because
 * the backend's studio permission check has none.
 */
export function useWorkspaceGrantCheck(): (permissions: readonly string[]) => boolean {
  return useScopedAllow('workspace', { adminOverride: false });
}

/**
 * True while the active workspace's permissions are resolving for the first time. Pass the
 * workspace the app expects to be active so the window before the app shell seeds the scope
 * also counts as resolving, instead of reading as an empty, denied list.
 */
export function useIsWorkspacePermissionsResolving(expectedWorkspaceId?: string | null): boolean {
  return useScopeResolving('workspace', expectedWorkspaceId);
}
